import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { emit } from '@/lib/analytics';
import { UnexpectedError, UserBlockingError } from '@hookmyapp/observability/errors';
import { captureFrontendError } from '@/lib/sentry';
import { isValidPublicId } from '@/lib/public-id';
import { writePopupContext } from '@/lib/meta-popup-context';
import { connectingPath, isInviteConnectPending } from '@/lib/invite-connect';

/**
 * /meta/callback/whatsapp — public landing page for WhatsApp Embedded Signup.
 *
 * 2026-05-23 refactor: WhatsApp ES moved from /meta/callback (shared with
 * Instagram via ?channelType=…) to its own dedicated /meta/callback/whatsapp
 * path. Sibling of /meta/callback/instagram and /meta/callback/instagram-fb.
 *
 * Meta redirect_uri lands here. The `state` URL param encodes
 * `${nonce}.${wsPublicId}` per the CONTEXT.md Meta-connect-flow spec
 * (commits cf34ade + 14f60db) — splitting on `.` is safe because nonce
 * is 32-hex and publicId is `ws_<8 base62>`, so neither can contain `.`.
 *
 * Nonce is server-validated at consume time (POST /meta/signup); this page
 * no longer touches browser storage for CSRF verification.
 *
 * Responsibilities:
 *   1. Parse code + state query params.
 *   2. Validate state format + publicId shape (cheap pre-checks).
 *   3. 3-path reconcile with wsPublicId sourced FROM state (not sessionStorage,
 *      not activeWorkspace):
 *      a) window.opener present  → postMessage({code, wsPublicId, state}) + close
 *      b) popup with broken opener → localStorage relay JSON {code, wsPublicId, state} + close
 *      c) direct nav              → navigate /w/:ws/channels/connecting?code=…&state=…
 */
export function MetaCallbackPage() {
  const navigate = useNavigate();
  // Guard against React 18/19 StrictMode double-invocation of useEffect in
  // dev. The first invocation consumes the URL params (parses state, fires
  // toast, navigates away). The second invocation would re-read the now-empty
  // URL and re-navigate, which can race-cancel the toast. One-shot gate.
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const returnedState = params.get('state');
    // 2026-05-23 refactor: channelType is implicit from the URL we're on
    // (/meta/callback/whatsapp). No more ?channelType query discriminator.
    const channelType = 'whatsapp';

    // Phase 113 — funnel stage 7 marker (113-CONTEXT.md §Canonical funnel
    // stages). Fires exactly once per /meta/callback mount; success reflects
    // whether the state+nonce handshake passed all pre-checks. The capture
    // is inlined at each terminal branch (Pattern B) so it fires BEFORE any
    // navigate/close/postMessage side effect swaps the page away — keeps it
    // in scope of the synchronous React effect tick.

    // Meta redirected back with an error instead of a code. The dominant case
    // is the user cancelling/declining the WhatsApp Embedded Signup consent
    // dialog (error=access_denied, error_reason=user_denied, action=cancel).
    // Per the CLAUDE.md error taxonomy that is an expected user-side condition
    // — a funnel drop-off, not an error. Recorded for analytics only; nothing
    // is sent to Sentry.
    // AIT-529 / Codex review round 14 (P2): an invited client must not be
    // dropped into the workspace flow when the OAuth return fails — they have
    // no workspace to land in, and the pending invite flag would still be set
    // for a later ordinary connect to trip over.
    const failureDestination = isInviteConnectPending() ? '/invite/connect' : '/';

    const oauthError = params.get('error');
    if (oauthError) {
      emit('meta_oauth_returned', {
        success: false,
        reason: oauthError,
        error_reason: params.get('error_reason') ?? undefined,
      });
      navigate(failureDestination, { replace: true });
      return;
    }

    // No code AND no error param — Meta never sends this on a real OAuth
    // redirect. Direct navigation, a stale bookmark, or a malformed
    // redirect_uri. Genuinely unexpected, but no specific human is mid-flow,
    // so it is a SEV2 capture — not the SEV1 render alias.
    if (!code) {
      captureFrontendError(
        new UnexpectedError({
          message: 'Meta callback reached with neither code nor error param',
          code: 'META_CALLBACK_NO_CODE_NO_ERROR',
        }),
      );
      emit('meta_oauth_returned', { success: false, reason: 'no_code' });
      navigate(failureDestination, { replace: true });
      return;
    }
    // Helper — defer BOTH the toast and the navigate to a subsequent macrotask.
    // Sonner's Toaster subscribes in its own useEffect (mount phase). On a cold
    // / first-paint page load of /meta/callback, the Toaster's subscribe may
    // not be attached yet when MetaCallbackPage's own useEffect fires. A single
    // macrotask defer lets the Toaster attach its listener before we push the
    // toast into the queue. The navigate is also deferred so the MetaCallbackPage
    // stays mounted long enough for React to commit the toast render before the
    // route change swaps the tree.
    const toastThenNav = (msg: string, to: string) => {
      setTimeout(() => {
        toast.error(msg);
        setTimeout(() => navigate(to, { replace: true }), 0);
      }, 0);
    };

    if (!returnedState) {
      captureFrontendError(
        new UserBlockingError({
          message: 'Meta callback received a code but no state',
          code: 'META_CALLBACK_STATE_MISSING',
        }),
      );
      emit('meta_oauth_returned', { success: false });
      toastThenNav(
        "We couldn't connect you to Meta right now. Please try again in a moment.",
        failureDestination,
      );
      return;
    }

    // state = `${nonce}.${wsPublicId}` per CONTEXT decision (commits cf34ade + 14f60db).
    const parts = returnedState.split('.');
    if (parts.length !== 2) {
      captureFrontendError(
        new UserBlockingError({
          message: 'Meta callback state is malformed (expected nonce.wsPublicId)',
          code: 'META_CALLBACK_STATE_MALFORMED',
        }),
      );
      emit('meta_oauth_returned', { success: false });
      toastThenNav(
        "We couldn't connect you to Meta right now. Please try again in a moment.",
        failureDestination,
      );
      return;
    }
    const [, wsPublicId] = parts;

    if (!isValidPublicId(wsPublicId, 'ws')) {
      captureFrontendError(
        new UserBlockingError({
          message: 'Meta callback state carried an invalid wsPublicId',
          code: 'META_CALLBACK_STATE_INVALID_WS',
        }),
      );
      emit('meta_oauth_returned', { success: false });
      toastThenNav(
        "We couldn't connect you to Meta right now. Please try again in a moment.",
        failureDestination,
      );
      return;
    }

    // All pre-checks passed — state shape + publicId shape are valid. The
    // nonce embedded in `state` is verified server-side at POST /meta/signup
    // consume time; this page no longer performs a CSRF check against
    // browser storage. Fire meta_oauth_returned BEFORE the 3-path
    // reconciliation (postMessage, localStorage relay, or direct nav) so the
    // capture queues before any window.close() tears down the page.
    emit('meta_oauth_returned', { success: true });

    // Three-path reconciliation — wsPublicId sourced FROM STATE, not sessionStorage.

    // Path 1 — popup with intact opener
    if (window.opener) {
      window.opener.postMessage(
        {
          type: 'META_SIGNUP_CODE',
          code,
          wsPublicId,
          state: returnedState, // forward the original state for backend consume
          channelType,
        },
        window.location.origin,
      );
      window.close();
      return;
    }

    // Path 2 — popup with broken opener (tunnel interstitial)
    const isProbablyPopup = window.history.length <= 2;
    if (isProbablyPopup) {
      localStorage.setItem(
        'meta_signup_code',
        JSON.stringify({ code, wsPublicId, state: returnedState, channelType }),
      );
      window.close();
      return;
    }

    // Path 3 — direct nav (mobile or pasted URL)
    writePopupContext(returnedState, {
      source: 'mobile_redirect',
      payload: null,
    });
    navigate(
      connectingPath(
        wsPublicId,
        `code=${encodeURIComponent(code)}&state=${encodeURIComponent(returnedState)}&channelType=${encodeURIComponent(channelType)}`,
      ),
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  return null; // pure transit page
}
